/**
 * Module: screens/flow/_tusUpload
 * Role: TUS resumable upload wrapper for Supabase Storage (BUG-1362).
 *       Supabase docs: chunkSize MUST be exactly 6MB, uses x-upsert header.
 *       Token from presign goes into x-signature header (signed branch).
 *
 * Uses: tus-js-client
 * Used by: VideoUploadBrick.tsx (video-kind only)
 */

import { Upload } from 'tus-js-client'

export interface TusUploadOptions {
  /** TUS endpoint URL (e.g. https://<project>.supabase.co/storage/v1/upload/resumable) */
  endpoint: string
  /** Signed upload token from presign (goes into x-signature header) */
  token: string
  /** Supabase Storage bucket name */
  bucket: string
  /** Object name (= artefact_path) */
  objectName: string
  /** File to upload */
  file: File
  /** Progress callback: ratio 0..1 */
  onProgress: (ratio: number) => void
  /** AbortSignal for cancellation */
  signal?: AbortSignal
}

// Supabase docs: chunkSize MUST be exactly 6 MB (do not change).
const TUS_CHUNK_SIZE = 6 * 1024 * 1024

// Retry delays for mobile: exponential backoff 0, 1s, 3s, 5s
const TUS_RETRY_DELAYS = [0, 1000, 3000, 5000]

/**
 * Upload a file via TUS resumable protocol to Supabase Storage.
 *
 * Resolves when upload completes. Rejects with Error on failure or
 * DOMException(AbortError) on abort.
 */
export function tusUpload(options: TusUploadOptions): Promise<void> {
  const { endpoint, token, bucket, objectName, file, onProgress, signal } =
    options

  return new Promise<void>((resolve, reject) => {
    // Abort handling
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'))
      return
    }

    const upload = new Upload(file, {
      endpoint,
      retryDelays: TUS_RETRY_DELAYS,
      chunkSize: TUS_CHUNK_SIZE,
      removeFingerprintOnSuccess: true,

      headers: {
        'x-signature': token,
        'x-upsert': 'true',
      },
      metadata: {
        bucketName: bucket,
        objectName,
        contentType: file.type,
        cacheControl: '3600',
      },

      onError(err: Error) {
        reject(err)
      },
      onProgress(bytesUploaded: number, bytesTotal: number) {
        const ratio = bytesTotal > 0 ? bytesUploaded / bytesTotal : 0
        onProgress(ratio)
      },
      onSuccess() {
        onProgress(1)
        resolve()
      },
    })

    // Wire abort signal
    const onAbort = () => {
      upload.abort(true).catch(() => {
        // abort cleanup errors are non-fatal
      })
      reject(new DOMException('Upload aborted', 'AbortError'))
    }
    signal?.addEventListener('abort', onAbort, { once: true })

    // Start (or resume from previous fingerprint)
    upload
      .findPreviousUploads()
      .then((previousUploads) => {
        if (previousUploads.length > 0) {
          upload.resumeFromPreviousUpload(previousUploads[0])
        }
        upload.start()
      })
      .catch((err: unknown) => {
        reject(err instanceof Error ? err : new Error(String(err)))
      })
  })
}
